import { Editor } from '@tiptap/react'
import { useMemo, useRef } from 'react'

import { RichTextEditorProps } from '~common/RichTextEditor/RichTextEditor'
// import { prepareContent } from '~common/RichTextEditor/utils'
import { paragraphJSON } from '~common/RichTextEditor/utils'
import { isLocal, isProduction } from '~lib/helpers'

type UseRichTextEditorResProps = {
  onCreate: RichTextEditorProps['onCreate']
  onDestroy: RichTextEditorProps['onDestroy']
}

type UseRichTextEditorResActions = {
  isActive: () => boolean
  isEmpty: () => boolean | undefined
  getContent: () => string | undefined // JSON string
  getJSON: () => IEditorContent | undefined
  getText: () => string | undefined
  getHTML: () => string | undefined
  getWords: () => number | undefined
  getCharacters: () => number | undefined
  clearContent: () => void
  replaceWithJSON: (content: IEditorContent) => void
  replaceWithText: (text: string) => void
  replaceWithHTML: (html: string, trigger?: boolean) => void
  appendJSON: (content: IEditorNode | IEditorNode[]) => void
  appendText: (text: string) => void
  appendHTML: (html: string) => void
  addLetter: (letter: string) => void
}

const alertEditorNotFound = (method: string) => {
  if (isProduction()) return

  const message = `[useRichTextEditor.${method}] editor is null. Seems you forgot to initialize the hook. Pass onCreate and onDestroy to RichTextEditor component`
  // eslint-disable-next-line no-alert
  if (isLocal()) window.alert(message)
  console.warn(message)
}

export function useRichTextEditor(): [UseRichTextEditorResProps, UseRichTextEditorResActions] {
  const editorRef = useRef<Editor | null>(null)

  const props = useMemo(() => {
    return {
      onCreate: (options: { editor: Editor }) => {
        editorRef.current = options.editor
      },
      onDestroy: () => {
        editorRef.current = null
      },
    }
  }, [])

  const actions = useMemo(() => {
    return {
      isActive: () => !!editorRef.current,
      isEmpty: () => {
        if (!editorRef.current) {
          return undefined
        }
        return editorRef.current.getText().trim().length === 0
      },
      getContent: () => {
        if (!editorRef.current) {
          alertEditorNotFound('getContent')
          return undefined
        }
        return JSON.stringify(editorRef.current.getJSON())
      },
      getJSON: () => {
        if (!editorRef.current) {
          alertEditorNotFound('getJSON')
          return undefined
        }
        return editorRef.current.getJSON() as IEditorContent
      },
      getText: () => {
        if (!editorRef.current) {
          alertEditorNotFound('getJSON')
          return undefined
        }
        return editorRef.current.getText()
      },
      getHTML: () => {
        if (!editorRef.current) {
          alertEditorNotFound('getJSON')
          return undefined
        }
        return editorRef.current.getHTML()
      },
      getWords: () => {
        if (!editorRef?.current) {
          alertEditorNotFound('getWords')
          return
        }
        /* eslint-disable consistent-return */
        return editorRef.current.storage.characterCount.words()
      },
      getCharacters: () => {
        if (!editorRef?.current) {
          alertEditorNotFound('getCharacters')
          return
        }
        /* eslint-disable consistent-return */
        return editorRef.current.storage.characterCount.characters()
      },
      replaceWithJSON: (content: IEditorContent) => {
        if (!editorRef.current) {
          alertEditorNotFound('replaceWithJSON')
          return
        }
        if (!content) {
          editorRef.current.commands.setContent(null)
          return
        }
        editorRef.current.commands.setContent(content)
      },
      replaceWithText: (text: string) => {
        if (!editorRef.current) {
          alertEditorNotFound('replaceWithText')
          return
        }
        if (!text) {
          editorRef.current.commands.setContent(null)
          return
        }

        const nodes: IEditorNode[] = text.split('/n/n').map(paragraphJSON)
        editorRef.current.commands.setContent(nodes)
      },
      replaceWithHTML: (html: string, trigger?: boolean) => {
        if (!editorRef.current) {
          alertEditorNotFound('replaceWithHTML')
          return
        }
        if (!html) {
          editorRef.current.commands.setContent(null)
          return
        }

        editorRef.current.commands.setContent(html, trigger, { preserveWhitespace: false })
      },
      appendJSON: (content: IEditorNode | IEditorNode[]) => {
        if (!editorRef.current) {
          alertEditorNotFound('appendContent')
          return
        }
        if (!content) return
        editorRef.current.commands.insertContent(content)
      },
      appendText: (text: string) => {
        if (!editorRef.current) {
          alertEditorNotFound('appendText')
          return
        }

        if (!text) return

        const nodes: IEditorNode[] = text.split('/n/n').map(paragraphJSON)
        editorRef.current.commands.insertContent(nodes)
      },
      appendHTML: (html: string) => {
        if (!editorRef.current) {
          alertEditorNotFound('appendHTML')
          return
        }
        if (!html) return
        editorRef.current.commands.insertContent(html, { parseOptions: { preserveWhitespace: false } })
      },
      addLetter: (letter: string) => {
        if (!editorRef.current) {
          alertEditorNotFound('addText')
          return
        }

        if (!letter) return
        editorRef.current.commands.insertContent(letter)
      },
      clearContent: () => {
        if (!editorRef.current) {
          alertEditorNotFound('clearContent')
          return
        }
        // Remove all content, and trigger the `update` event
        editorRef.current.commands.clearContent(true)
      },
    }
  }, [])

  return [props, actions]
}

export default useRichTextEditor
